import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import ManageColumns from "../../../components/filters/ManageColumns";
import Table from "../../../core/common/dataTable/index";
import { Modal } from "react-bootstrap";
import { CompanyService, Company } from "./company.service";
import CompanyForm from "./CompanyForm";
import "./CompanyForm.css";
import { AuthContext } from "../../../context/authcontext";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import { sortRecords } from "../../../utils/sortUtils";
import {
  showSuccessAlert,
  showErrorAlert,
  showConfirmationAlert,
} from "../../uiInterface/base-ui/alertservice";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import male from "../../../../src/assets/male.png";
import TableFilters, {
  FilterState,
  FilterConfig,
  mergeTableFilterChanges,
} from "../../../components/filters/TableFilters";
import { API_BASE_URL } from "../../../environment";
import SortableHeader from "../../../components/filters/SortableHeader";
import SortByDropdown from "../../../components/filters/SortByDropdown";
import ListFilterChips, {
  getClearedListFilters,
} from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import company from "../../../../src/assets/company.png";
import ExportComponent from "../../../components/ExportComponent";
import CompanyContactFilterDropdown from "../components/CompanyContactFilterDropdown";
import Tooltip from "@mui/material/Tooltip";
import EntityDetailsModal, {
  EntityDetailSection,
} from "../../../components/EntityDetailsModal/EntityDetailsModal";
import { openOffcanvas } from "../../../utils/offcanvasUtils";

const refreshIconStyles = `
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .ti-spin {
    animation: spin 1s linear infinite;
  }

  .custom-pagination .ant-pagination-item {
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    margin: 0 2px;
  }

  .custom-pagination .ant-pagination-item:hover {
    border-color: #007bff;
  }

  .custom-pagination .ant-pagination-item-active {
    background-color: #007bff;
  }

  .custom-pagination .ant-pagination-item-active a {
    color: white;
  }

  .custom-pagination .ant-pagination-prev,
  .custom-pagination .ant-pagination-next {
    border: none;
    margin: 0 2px;
  }

  .custom-pagination .ant-pagination-prev:hover,
  .custom-pagination .ant-pagination-next:hover {
    color: #007bff;
  }

  .custom-pagination .ant-pagination-total-text {
    color: #6b7280;
    font-size: 14px;
  }

  .company-logo {
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .company-logo:hover {
    transform: scale(1.05);
  }

  .custom-pagination {
    margin: 16px 0;
  }
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = refreshIconStyles;
  if (!document.head.querySelector("style[data-refresh-icon]")) {
    styleElement.setAttribute("data-refresh-icon", "true");
    document.head.appendChild(styleElement);
  }
}

interface CompaniesListViewProps {
  companies: any[];
  loading: boolean;
  backgroundLoading?: boolean;
  error: string | null;
  pagination: any;
  fetchCompanies: (page?: number) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  addCompany: (company: any) => void;
  updateCompany: (company: any) => void;
  removeCompany: (companyId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  currentView: "list" | "grid";
  onViewSwitch: (view: "list" | "grid") => void;
  clearBackgroundLoading?: () => void;
}

const CompaniesListView = React.memo(
  ({
    companies,
    loading,
    backgroundLoading = false,
    error,
    pagination,
    fetchCompanies,
    refreshCompanies,
    addCompany,
    updateCompany,
    removeCompany,
    lastFetched,
    isInitialized,
    currentView,
    onViewSwitch,
    clearBackgroundLoading,
    ...rest
  }: CompaniesListViewProps) => {
    const route = all_routes;
    const navigate = useNavigate();
    const { selectedTenantId, auth } = useContext(AuthContext);
    const [openModal, setOpenModal] = useState(false);

    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [detailsCompany, setDetailsCompany] = useState<any>(null);
    const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
    const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deleteError, setDeleteError] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);

    const [filters, setFilters] = useState<FilterState>({
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc",
    });

    const [additionalFilters, setAdditionalFilters] = useState<Record<string, any>>({});

    const defaultColumnVisibility = {
      company_name: true,
      country: true,
      employee_count: true,
      industry_type_display_name: true,
      assigned_user: true,
      created_datetime: true,
      assigned_team: false,
      website: false,
      phone: false,
      address: false,
      city: false,
      state: false,
      annual_revenue: false,
      updated_datetime: false,
      linkedin_url: false,
      facebook_url: false,
      twitter_url: false,
      business_type_display_name: false,
      created_by_username: false,
      updated_by_username: false,
    };

    const [columnVisibility, setColumnVisibility] = useState<{
      [key: string]: boolean;
    }>(() => {
      try {
        const saved = localStorage.getItem("companies_column_visibility");
        if (saved) {
          const parsed = JSON.parse(saved);

          const visibility: { [key: string]: boolean } = {
            ...defaultColumnVisibility,
          };

          Object.keys(parsed).forEach((key) => {
            if (key in defaultColumnVisibility) {
              visibility[key] = parsed[key] === true;
            }
          });

          const visibleCount = Object.values(visibility).filter(Boolean).length;
          if (visibleCount === 0) {
            const defaultKeys = Object.keys(defaultColumnVisibility);
            for (const key of defaultKeys) {
              if (
                defaultColumnVisibility[
                key as keyof typeof defaultColumnVisibility
                ]
              ) {
                visibility[key] = true;
                break;
              }
            }
          }

          return visibility;
        }
      } catch (error) {
        console.error(
          "Error loading column visibility from localStorage:",
          error
        );
      }
      return defaultColumnVisibility;
    });

    const saveColumnVisibilityToStorage = useCallback(
      (visibility: { [key: string]: boolean }) => {
        try {
          localStorage.setItem(
            "companies_column_visibility",
            JSON.stringify(visibility)
          );
        } catch (error) {
          console.error(
            "Error saving column visibility to localStorage:",
            error
          );
        }
      },
      []
    );

    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const [filteredCompaniesData, setFilteredCompaniesData] = useState<any[]>(
      []
    );
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const [filteredTotalCount, setFilteredTotalCount] = useState(0);

    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    useEffect(() => { }, []);

    useEffect(() => {
      if (
        !isFilterActive &&
        pagination.currentPage &&
        pagination.currentPage !== currentPage
      ) {
        setCurrentPage(pagination.currentPage);
      }
    }, [pagination.currentPage, currentPage, isFilterActive]);

    const clearMessages = () => {
      setDeleteError("");
    };

    const filterConfig: FilterConfig = useMemo(
      () => ({
        showSearch: true,
        searchPlaceholder: "Search companies...",
        searchField: "company_name",
        showDateFilter: true,
        dateField: "created_datetime",
        showIndustryFilter: true,
        industryField: "industry_type_display_name",
        showCountryFilter: true,
        countryField: "country",
        pageName: "company" as const,
        defaultSort: {
          field: "created_datetime",
          order: "desc" as const,
        },
      }),
      []
    );

    const handleApiFilterChange = useCallback(
      (apiFilters: Record<string, any>) => { },
      []
    );

    const handleApiCall = useCallback(
      async (url: string) => {
        if (!selectedTenantId) {
          return;
        }

        try {
          setIsFilterLoading(true);

          const headers = getAuthHeaders();

          const response = await fetch(url, {
            method: "GET",
            headers: headers,
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data.results && Array.isArray(data.results)) {
            const normalizeCompanyData = (company: any) => {
              const normalizeUserObject = (
                userData: any,
                fieldName: string
              ) => {
                if (!userData) return null;

                if (typeof userData === "object") {
                  if (fieldName === "assigned_user") {
                    if (userData.full_name || userData.user_id) {
                      return userData;
                    }
                  } else {
                    if (userData.full_name || userData.user_id) {
                      return userData;
                    }
                  }
                }

                if (typeof userData === "number") {
                  return userData;
                }

                return null;
              };

              return {
                ...company,
                assigned_user: normalizeUserObject(
                  company.assigned_user,
                  "assigned_user"
                ),
                assigned_team: company.assigned_team || null,
              };
            };

            const normalizedResults = data.results.map(normalizeCompanyData);
            setFilteredCompaniesData(normalizedResults);
            setFilteredTotalCount(data.count || normalizedResults.length);
            setIsFilterActive(true);
          } else {
            setFilteredCompaniesData([]);
            setFilteredTotalCount(0);
            setIsFilterActive(true);
          }
        } catch (error: any) {
          try {
            handleAuthError(error);
          } catch (authError) {
            return;
          }

          setFilteredCompaniesData([]);
          setFilteredTotalCount(0);
          setIsFilterActive(false);
        } finally {
          setIsFilterLoading(false);
        }
      },
      [selectedTenantId]
    );

    const buildCompaniesFilterUrl = useCallback(
      (
        page: number,
        nextFilters: FilterState,
        nextAdditional: Record<string, any>
      ) => {
        const params = new URLSearchParams();
        params.set("tenant_id", selectedTenantId || "");
        params.set("page", page.toString());
        params.set("page_size", "12");

        if (nextFilters.searchTerm) {
          params.set("company_name", nextFilters.searchTerm);
        }

        const [startDate, endDate] = nextFilters.dateRange;
        if (startDate && endDate) {
          const start = startDate.toISOString().split("T")[0];
          const end = endDate.toISOString().split("T")[0];
          const dayDifference =
            (new Date(end).getTime() - new Date(start).getTime()) /
            (1000 * 60 * 60 * 24);

          if (start === end || dayDifference <= 1) {
            params.set("created_datetime", end);
          } else {
            params.set("created_datetime_gte", start);
            params.set("created_datetime_lte", end);
          }
        } else if (startDate) {
          params.set("created_datetime_gte", startDate.toISOString().split("T")[0]);
        } else if (endDate) {
          params.set("created_datetime_lte", endDate.toISOString().split("T")[0]);
        }

        nextFilters.industryFilter?.forEach((industry) => {
          params.append("industry_type_display_name", industry);
        });

        nextFilters.countryFilter?.forEach((country) => {
          params.append("country", country);
        });

        Object.entries(nextAdditional).forEach(([key, value]) => {
          if (value === null || value === undefined || value === "") return;
          if (key === "teamFilter") {
            params.set("assigned_team_id", value.toString());
          } else if (key === "userFilter") {
            params.set("assigned_user_id", value.toString());
          } else if (key === "sourceFilter") {
            params.set("source", value.toString());
          } else {
            params.set(key, value.toString());
          }
        });

        if (nextFilters.sort_by) {
          params.set("sort_by", nextFilters.sort_by);
        }

        return `${API_BASE_URL}/company/?${params.toString()}`;
      },
      [selectedTenantId]
    );

    const loadCompanies = useCallback(
      (
        page: number,
        nextFilters: FilterState,
        nextAdditional: Record<string, any>
      ) => {
        if (!selectedTenantId) return;

        const hasActiveFilters =
          !!nextFilters.searchTerm ||
          !!nextFilters.dateRange[0] ||
          !!nextFilters.dateRange[1] ||
          nextFilters.industryFilter.length > 0 ||
          nextFilters.countryFilter.length > 0 ||
          !!nextFilters.sort_by ||
          Object.values(nextAdditional).some(
            (value) => value !== null && value !== undefined && value !== ""
          );

        if (!hasActiveFilters) {
          setIsFilterActive(false);
          setFilteredCompaniesData([]);
          setFilteredTotalCount(0);
          fetchCompanies(page);
          return;
        }

        handleApiCall(buildCompaniesFilterUrl(page, nextFilters, nextAdditional));
      },
      [selectedTenantId, handleApiCall, buildCompaniesFilterUrl, fetchCompanies]
    );

    const handleFilterChange = useCallback(
      (newFilters: FilterState) => {
        const mergedFilters = mergeTableFilterChanges(filters, newFilters);
        setFilters(mergedFilters);
        setCurrentPage(1);
        loadCompanies(1, mergedFilters, additionalFilters);
      },
      [filters, additionalFilters, loadCompanies]
    );

    const handlePageChange = useCallback(
      (page: number) => {
        setCurrentPage(page);
        loadCompanies(page, filters, additionalFilters);
      },
      [filters, additionalFilters, loadCompanies]
    );

    const handleAdditionalFilterChange = useCallback(
      (dynamicFilters: Record<string, any>) => {
        setAdditionalFilters(dynamicFilters);
        setCurrentPage(1);
        loadCompanies(1, filters, dynamicFilters);
      },
      [filters, loadCompanies]
    );

    const applyListFilters = useCallback(
      (nextFilters: FilterState, nextAdditional: Record<string, any>) => {
        setFilters(nextFilters);
        setAdditionalFilters(nextAdditional);
        setCurrentPage(1);
        loadCompanies(1, nextFilters, nextAdditional);
      },
      [loadCompanies]
    );

    const handleRemoveListFilters = useCallback(
      (patch: Record<string, any>) => {
        const filterPatch: Record<string, any> = {};
        const additionalPatch: Record<string, any> = {};
        Object.entries(patch).forEach(([key, value]) => {
          if (key in filters) {
            filterPatch[key] = value;
          } else {
            additionalPatch[key] = value;
          }
        });
        applyListFilters(
          { ...filters, ...filterPatch },
          { ...additionalFilters, ...additionalPatch }
        );
      },
      [filters, additionalFilters, applyListFilters]
    );

    const handleSortChange = (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
      setCurrentPage(1);
    };

    const availableColumns = [
      { key: "company_name", label: "Company Name" },
      { key: "country", label: "Country" },
      { key: "employee_count", label: "Employee Count" },
      { key: "industry_type_display_name", label: "Industry Type" },
      { key: "assigned_user", label: "Assigned User" },
      { key: "created_datetime", label: "Created Date" },
      { key: "business_type_display_name", label: "Business Type" },
      { key: "created_by_username", label: "Created By" },
      { key: "assigned_team", label: "Assigned Team" },
      { key: "updated_by_username", label: "Updated By" },
      { key: "website", label: "Website" },
      { key: "phone", label: "Phone" },
      { key: "address", label: "Address" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "annual_revenue", label: "Annual Revenue" },
      { key: "updated_datetime", label: "Updated Date" },
      { key: "linkedin_url", label: "LinkedIn URL" },
      { key: "facebook_url", label: "Facebook URL" },
      { key: "twitter_url", label: "Twitter URL" },
    ];

    const closeOffcanvas = useCallback((offcanvasId: string) => {
      const modal = document.getElementById(offcanvasId);
      if (modal) {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(modal);
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        } else {
          const closeButton = modal.querySelector('[data-bs-dismiss="offcanvas"]') as HTMLElement;
          if (closeButton) closeButton.click();
        }
      }
    }, []);

    const [orderedAvailableColumns, setOrderedAvailableColumns] = useState(() => {
      const storageKey = `${window.location.pathname}_column_order`;
      const savedOrder = localStorage.getItem(storageKey);
      if (savedOrder) {
        try {
          const orderKeys = JSON.parse(savedOrder);
          return [...availableColumns].sort((a, b) => {
            const indexA = orderKeys.indexOf(a.key);
            const indexB = orderKeys.indexOf(b.key);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
        } catch (e) {
          return [...availableColumns];
        }
      }
      return [...availableColumns];
    });

    const getSortedAvailableColumns = () => {
      return orderedAvailableColumns;
    };

    const { filteredCompanies, totalFilteredCount } = useMemo(() => {
      if (isFilterActive) {
        return {
          filteredCompanies: filteredCompaniesData,
          totalFilteredCount: filteredTotalCount,
        };
      }

      if (!Array.isArray(companies)) {
        return { filteredCompanies: [], totalFilteredCount: 0 };
      }

      return {
        filteredCompanies: companies,
        totalFilteredCount: pagination.totalCount,
      };
    }, [
      companies,
      filteredCompaniesData,
      isFilterActive,
      pagination.totalCount,
      filteredTotalCount,
    ]);

    const paginatedCompanies = useMemo(
      () =>
        sortRecords<any>(filteredCompanies, filters.sortBy, filters.sortOrder, {
          created_by_username: (record) =>
            record.created_by?.full_name || record.created_by_username,
          updated_by_username: (record) =>
            record.updated_by?.full_name || record.updated_by_username,
        }),
      [filteredCompanies, filters.sortBy, filters.sortOrder]
    );

    const handleEdit = useCallback((e: React.MouseEvent, company: any) => {
      if (!selectedTenantId) {
        e.stopPropagation();
        e.preventDefault();
        showErrorAlert("Please select a tenant before editing companies.");
        return;
      }
      clearMessages();
      flushSync(() => {
        setSelectedCompany(company);
        setIsEditCompanyOpen(true);
      });
    }, [selectedTenantId]);

    const handleEditSubmit = async (formData: any) => {
      if (!selectedCompany || isUpdating) return;

      if (!selectedTenantId) {
        showErrorAlert("Please select a tenant before updating companies.");
        return;
      }

      clearMessages();
      setIsUpdating(true);
      setIsSubmitting(true);
      setIsLoading(true);

      try {
        const logoFile = formData.logoFile;
        delete formData.logoFile;

        const dataToSend = Object.entries(formData).reduce(
          (acc, [key, value]) => {
            if (key === "created_by") {
              acc[key] = value;
            } else if (value !== "" && value !== null && value !== undefined) {
              acc[key] = value;
            }
            return acc;
          },
          {} as any
        );

        let updatedCompany: Company | undefined;
        if (logoFile) {
          const uploadFormData = new FormData();
          Object.entries(dataToSend).forEach(([k, v]) =>
            uploadFormData.append(k, String(v))
          );
          uploadFormData.append("company_logo", logoFile);

          updatedCompany = await CompanyService.updateCompanyWithLogo(
            selectedCompany.company_id,
            uploadFormData
          );
        } else {
          updatedCompany = await CompanyService.updateCompany(
            selectedCompany.company_id,
            dataToSend
          );
        }

        if (!updatedCompany) {
          throw new Error("Failed to update company");
        }

        const companyName = updatedCompany.company_name;

        const companyWithTimestamp = {
          ...updatedCompany,
          created_at:
            updatedCompany.created_at ||
            updatedCompany.created_datetime ||
            selectedCompany.created_at,
          updated_at:
            updatedCompany.updated_at ||
            updatedCompany.updated_datetime ||
            new Date().toISOString(),
          created_datetime:
            updatedCompany.created_datetime ||
            updatedCompany.created_at ||
            selectedCompany.created_datetime,
          updated_datetime:
            updatedCompany.updated_datetime ||
            updatedCompany.updated_at ||
            new Date().toISOString(),
        };

        updateCompany(companyWithTimestamp);

        if (isFilterActive) {
          setFilteredCompaniesData((prev) =>
            prev.map((company) =>
              company.company_id === selectedCompany.company_id
                ? companyWithTimestamp
                : company
            )
          );
        }

        setSelectedCompany(null);

        closeOffcanvas("offcanvas_edit");

        setTimeout(() => {
          setIsLoading(false);
          showSuccessAlert(`Company "${companyName}" updated successfully!`);
        }, 1000);
      } catch (err: any) {
        console.error("Update error:", err);
        setIsLoading(false);

        closeOffcanvas("offcanvas_edit");

        if (err.response?.status === 400) {
          showErrorAlert("Invalid data provided. Please check your inputs.");
        } else if (err.response?.status === 401) {
          showErrorAlert("Authentication failed. Please login again.");
        } else if (err.response?.status === 403) {
          showErrorAlert("You do not have permission to update this company.");
        } else if (err.response?.status >= 500) {
          showErrorAlert("Server error. Please try again later.");
        } else {
          showErrorAlert("Failed to update company. Please try again.");
        }
        throw err;
      } finally {
        setIsUpdating(false);
        setIsSubmitting(false);
        setIsLoading(false);
      }
    };

    const handleDelete = async (company: any) => {
      if (!selectedTenantId) {
        showErrorAlert("Please select a tenant before deleting companies.");
        return;
      }

      const result = await showConfirmationAlert(
        "Delete Company",
        `Are you sure you want to delete "${company.company_name}"? This action cannot be undone.`,
        async () => {
          setIsDeleting(true);
          setIsLoading(true);
          try {
            await CompanyService.deleteCompany(company.company_id);
          } finally {
            setIsDeleting(false);
            setIsLoading(false);
          }
        }
      );

      if (result.isConfirmed && result.value) {
        removeCompany(company.company_id);

        if (isFilterActive) {
          setFilteredCompaniesData((prev) =>
            prev.filter((c) => c.company_id !== company.company_id)
          );
        }

        showSuccessAlert(`Company "${company.company_name}" deleted successfully!`);

        setSelectedCompany(null);
      }
    };

    const handleBulkDelete = async () => {
      if (selectedItems.length === 0) return;

      const result = await showConfirmationAlert(
        "Delete Selected Records",
        `Are you sure you want to delete ${selectedItems.length} selected record${selectedItems.length > 1 ? "s" : ""}? This action cannot be undone.`,
        async () => {
          setIsBulkDeleting(true);
          try {
            const deletePromises = selectedItems.map((companyId) =>
              CompanyService.deleteCompany(companyId)
            );
            await Promise.all(deletePromises);
          } finally {
            setIsBulkDeleting(false);
          }
        }
      );

      if (result.isConfirmed && result.value) {
        selectedItems.forEach((companyId) => removeCompany(companyId));

        if (isFilterActive) {
          setFilteredCompaniesData((prev) =>
            prev.filter(
              (company) => !selectedItems.includes(company.company_id)
            )
          );
        }

        setSelectedItems([]);
        setSelectAll(false);

        await showSuccessAlert(
          `${selectedItems.length} compan${selectedItems.length > 1 ? "ies" : "y"
          } deleted successfully!`
        );
      }
    };;

    const createCompanyUrl = (company: any): string => {
      return `/companies/${company.company_id}`;
    };

    const handlePreview = (company: any) => {
      try {
        setIsPreviewing(true);

        const dropdowns = document.querySelectorAll(".dropdown-menu.show");
        dropdowns.forEach((dropdown) => {
          dropdown.classList.remove("show");
        });

        const companyUrl = createCompanyUrl(company);
        navigate(companyUrl);
      } catch (error) {
        console.error("Error navigating to company details:", error);

        const companyUrl = createCompanyUrl(company);
        window.location.href = companyUrl;
      } finally {
        setIsPreviewing(false);
      }
    };

    const handleCreateSubmit = async (formData: any) => {
      if (isCreating) return;

      if (!selectedTenantId) {
        showErrorAlert("Please select a tenant before creating companies.");
        return;
      }

      clearMessages();
      setIsCreating(true);
      setIsSubmitting(true);
      setIsLoading(true);

      try {
        const logoFile = formData.logoFile;
        delete formData.logoFile;

        const dataToSend = Object.entries(formData).reduce(
          (acc, [key, value]) => {
            if (key === "created_by") {
              acc[key] = value;
            } else if (value !== "" && value !== null && value !== undefined) {
              acc[key] = value;
            }
            return acc;
          },
          {} as any
        );

        let newCompany;
        if (logoFile) {
          const uploadFormData = new FormData();
          Object.entries(dataToSend).forEach(([k, v]) => {
            if (k === "custom_fields" && typeof v === "string") {
              uploadFormData.append(k, v);
            } else if (k === "tags" && typeof v === "object") {
              uploadFormData.append(k, JSON.stringify(v));
            } else {
              uploadFormData.append(k, String(v));
            }
          });
          uploadFormData.append("company_logo", logoFile);

          newCompany = await CompanyService.createCompanyWithLogo(
            uploadFormData
          );
        } else {
          newCompany = await CompanyService.createCompany(dataToSend);
        }

        const companyWithTimestamp = {
          ...newCompany,
          created_at:
            newCompany.created_at ||
            newCompany.created_datetime ||
            new Date().toISOString(),
          updated_at:
            newCompany.updated_at ||
            newCompany.updated_datetime ||
            new Date().toISOString(),
          created_datetime:
            newCompany.created_datetime ||
            newCompany.created_at ||
            new Date().toISOString(),
          updated_datetime:
            newCompany.updated_datetime ||
            newCompany.updated_at ||
            new Date().toISOString(),
        };

        addCompany(companyWithTimestamp);

        if (isFilterActive) {
          setFilteredCompaniesData((prev) => [companyWithTimestamp, ...prev]);
        }

        closeOffcanvas("add_company");

        showSuccessAlert(
          `Company "${newCompany.company_name}" created successfully!`
        );

        setTimeout(() => {
          const companyUrl = createCompanyUrl(companyWithTimestamp);
          navigate(companyUrl);
          setIsLoading(false);
        }, 500);
      } catch (err: any) {
        console.error("Create error:", err);
        setIsLoading(false);

        closeOffcanvas("add_company");

        if (err.response?.status === 400) {
          showErrorAlert("Invalid data provided. Please check your inputs.");
        } else if (err.response?.status === 401) {
          showErrorAlert("Authentication failed. Please login again.");
        } else if (err.response?.status === 403) {
          showErrorAlert("You do not have permission to create companies.");
        } else if (err.response?.status >= 500) {
          showErrorAlert("Server error. Please try again later.");
        } else {
          showErrorAlert("Failed to create company. Please try again.");
        }
        throw err;
      } finally {
        setIsCreating(false);
        setIsSubmitting(false);
        setIsLoading(false);
      }
    };

    const handleColumnVisibilityChange = (
      columnKey: string,
      isVisible: boolean
    ) => {
      const currentVisibleCount =
        Object.values(columnVisibility).filter(Boolean).length;

      if (isVisible && currentVisibleCount >= 6) {
        showErrorAlert(
          "Maximum 6 columns can be enabled at once. Please disable another column first."
        );
        return;
      }

      if (!isVisible && currentVisibleCount <= 1) {
        showErrorAlert("At least one column must remain visible.");
        return;
      }

      const newColumnVisibility = {
        ...columnVisibility,
        [columnKey]: isVisible,
      };

      setColumnVisibility(newColumnVisibility);
      saveColumnVisibilityToStorage(newColumnVisibility);
    };

    const handleResetColumns = () => {
      setColumnVisibility(defaultColumnVisibility);
      saveColumnVisibilityToStorage(defaultColumnVisibility);

      const defaultFilters: FilterState = {
        searchTerm: "",
        dateRange: [null, null] as [Date | null, Date | null],
        industryFilter: [],
        countryFilter: [],
        statusFilter: null,
        sortBy: "created_datetime",
        sortOrder: "desc",
      };
      setFilters(defaultFilters);
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setSelectAll(checked);
      if (checked) {
        const validIds = filteredCompanies
          .filter((item) => item.company_id)
          .map((item) => item.company_id as number);
        setSelectedItems(validIds);
      } else {
        setSelectedItems([]);
      }
    };

    const handleSelectItem = (companyId: number) => {
      setSelectedItems((prev) => {
        const newSelected = prev.includes(companyId)
          ? prev.filter((id) => id !== companyId)
          : [...prev, companyId];

        const validCurrentData = filteredCompanies.filter(
          (item) => item.company_id
        );
        setSelectAll(
          newSelected.length === validCurrentData.length &&
          validCurrentData.length > 0
        );
        return newSelected;
      });
    };

    useEffect(() => {
      if (filteredCompanies.length > 0) {
        const validCurrentData = filteredCompanies.filter(
          (item) => item.company_id
        );
        const allCurrentSelected = validCurrentData.every(
          (item) => item.company_id && selectedItems.includes(item.company_id)
        );
        setSelectAll(allCurrentSelected && selectedItems.length > 0);
      } else {
        setSelectAll(false);
      }
    }, [filteredCompanies.length, selectedItems.length]);

    useEffect(() => {
      saveColumnVisibilityToStorage(columnVisibility);
    }, [columnVisibility, saveColumnVisibilityToStorage]);

    useEffect(() => {
      const addOffcanvas = document.getElementById("add_company");

      const handleHidden = () => {
        setIsAddCompanyOpen(false);
      };

      if (addOffcanvas) {
        addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
      }

      return () => {
        if (addOffcanvas) {
          addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
        }
      };
    }, []);

    useEffect(() => {
      const editOffcanvas = document.getElementById("offcanvas_edit");

      const handleHidden = () => {
        setIsEditCompanyOpen(false);
      };

      if (editOffcanvas) {
        editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
      }

      return () => {
        if (editOffcanvas) {
          editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
        }
      };
    }, []);

    const allAvailableColumns = useMemo(
      () => [
        {
          title: (
            <SortableHeader
              title="Company Name"
              field="company_name"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "company_name",
          key: "company_name",
          render: (text: any, record: any) => (
            <h2 className="d-flex align-items-center">
              <Link
                to={createCompanyUrl(record)}
                className="d-flex align-items-center"
              >
                <div
                  className="avatar avatar-sm border rounded flex-shrink-0 me-2"
                  style={{ borderRadius: "50%" }}
                >
                  {record.company_logo ? (
                    <img
                      src={record.company_logo}
                      className="w-auto h-auto"
                      alt={`${record.company_name} logo`}
                      style={{
                        width: "32px",
                        height: "32px",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector(".fallback-icon")) {
                          const fallbackDiv = document.createElement("div");
                          fallbackDiv.className =
                            "d-flex align-items-center justify-content-center w-100 h-100 bg-light fallback-icon";
                          fallbackDiv.innerHTML =
                            '<i class="ti ti-building" style="font-size: 1rem; color: #6c757d;"></i>';
                          parent.appendChild(fallbackDiv);
                        }
                      }}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light">
                      <img
                        src={company}
                        className="w-auto h-auto"
                        alt={`${record.company_name} logo`}
                        style={{
                          width: "32px",
                          height: "32px",
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="d-flex flex-column fw-medium">
                  {record.company_name && record.company_name.length > 20 ? (
                    <Tooltip title={record.company_name} placement="top" arrow>
                      <p className="mb-1" style={{ cursor: "pointer" }}>
                        {`${record.company_name.substring(0, 20)}...`}
                      </p>
                    </Tooltip>
                  ) : (
                    <p className="mb-1">{record.company_name || "-"}</p>
                  )}
                </div>
              </Link>
            </h2>
          ),
        },
        {
          title: (
            <SortableHeader
              title="Country"
              field="country"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "country",
          key: "country",
          render: (text: any, record: any) => record.country || "-",
        },
        {
          title: (
            <SortableHeader
              title="Employee Count"
              field="employee_count"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "employee_count",
          key: "employee_count",
          render: (text: any, record: any) => record.employee_count || "-",
          width: "14%",
        },
        {
          title: (
            <SortableHeader
              title="Industry Type"
              field="industry_type_display_name"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "industry_type_display_name",
          key: "industry_type_display_name",
          width: "14%",
          render: (text: any, record: any) => (
            <span>{record.industry_type_display_name || "Not Set"}</span>
          ),
        },
        {
          title: (
            <SortableHeader
              title="Assigned Team"
              field="assigned_team"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "assigned_team",
          key: "assigned_team",
          width: "14%",
          render: (text: any, record: any) => {
            const team = record.assigned_team;
            if (!team) return <span>No Assigned Team</span>;
            return (
              <span className="d-flex align-items-center">
                <i className="ti ti-users me-2" style={{ color: "#6c757d" }} />
                <span>{team.team_name || team.name || "No Assigned Team"}</span>
              </span>
            );
          },
        },
        {
          title: (
            <SortableHeader
              title="Assigned User"
              field="assigned_user"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "assigned_user",
          key: "assigned_user",
          width: "14%",
          render: (text: any, record: any) => {
            const user = record.assigned_user;

            if (!user) {
              return (
                <span className="d-flex align-items-center">
                  <i className="ti ti-user" style={{ color: "#6c757d" }} />{" "}
                  &nbsp;
                  <span>No Assigned User</span>
                </span>
              );
            }

            let fullName = "";
            let avatarUrl = male;

            if (typeof user === "object") {
              fullName = user.full_name || user.name || "";
              if (user.avatar_url && user.avatar_url.trim() !== "") {
                avatarUrl = user.avatar_url;
              }
            } else if (typeof user === "string") {
              fullName = user;
            } else if (typeof user === "number") {
              fullName = `User ID: ${user}`;
            }

            if (!fullName || fullName.trim() === "") {
              return (
                <span className="d-flex align-items-center">
                  <i className="ti ti-user" style={{ color: "#6c757d" }} />{" "}
                  &nbsp;
                  <span>No Assigned User</span>
                </span>
              );
            }

            return (
              <span className="d-flex align-items-center">
                <img
                  src={avatarUrl}
                  alt={fullName}
                  className="me-2 rounded-circle"
                  style={{ width: 24, height: 24, objectFit: "cover" }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = male;
                  }}
                />
                <span>{fullName}</span>
              </span>
            );
          },
        },
        {
          title: (
            <SortableHeader
              title="Created Date"
              field="created_datetime"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "created_datetime",
          key: "created_datetime",
          width: "14%",
          render: (text: any) => <span>{formatCustomDateTime(text)}</span>,
        },
        {
          title: (
            <SortableHeader
              title="Business Type"
              field="business_type_display_name"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "business_type_display_name",
          key: "business_type_display_name",
          width: "14%",
          render: (text: any, record: any) => (
            <span>{record.business_type_display_name || "Not Set"}</span>
          ),
        },
        {
          title: (
            <SortableHeader
              title="Website"
              field="website"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "website",
          key: "website",
          render: (text: any, record: any) => record.website || "-",
        },
        {
          title: (
            <SortableHeader
              title="Phone"
              field="phone"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "phone",
          key: "phone",
          render: (text: any, record: any) => record.phone || "-",
        },
        {
          title: (
            <SortableHeader
              title="Address"
              field="address"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "address",
          key: "address",
          render: (text: any, record: any) => {
            const addressParts = [
              record.address,
              record.city,
              record.state,
            ].filter((part) => part && part.trim() !== "");

            return addressParts.length > 0 ? addressParts.join(", ") : "-";
          },
        },
        {
          title: (
            <SortableHeader
              title="City"
              field="city"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "city",
          key: "city",
          render: (text: any, record: any) => record.city || "-",
        },
        {
          title: (
            <SortableHeader
              title="State"
              field="state"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "state",
          key: "state",
          render: (text: any, record: any) => record.state || "-",
        },
        {
          title: (
            <SortableHeader
              title="Annual Revenue"
              field="annual_revenue"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "annual_revenue",
          key: "annual_revenue",
          render: (text: any, record: any) => record.annual_revenue || "-",
        },
        {
          title: (
            <SortableHeader
              title="Created By"
              field="created_by_username"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "created_by_username",
          key: "created_by_username",
          width: "14%",
          render: (text: any, record: any) => {
            if (typeof record.created_by === "object" && record.created_by) {
              return record.created_by.full_name || "N/A";
            }
            return (
              record.created_by_username ||
              (record.created_by ? `User ID: ${record.created_by}` : "N/A")
            );
          },
        },
        {
          title: (
            <SortableHeader
              title="Updated By"
              field="updated_by_username"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "updated_by_username",
          key: "updated_by_username",
          width: "14%",
          render: (text: any, record: any) => {
            if (typeof record.updated_by === "object" && record.updated_by) {
              return record.updated_by.full_name || "N/A";
            }
            return (
              record.updated_by_username ||
              (record.updated_by ? `User ID: ${record.updated_by}` : "N/A")
            );
          },
        },
        {
          title: (
            <SortableHeader
              title="Updated Date"
              field="updated_datetime"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "updated_datetime",
          key: "updated_datetime",
          width: "14%",
          render: (text: any) => <span>{formatCustomDateTime(text)}</span>,
        },
        {
          title: (
            <SortableHeader
              title="LinkedIn URL"
              field="linkedin_url"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "linkedin_url",
          key: "linkedin_url",
          width: "16%",
        },
        {
          title: (
            <SortableHeader
              title="Facebook URL"
              field="facebook_url"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "facebook_url",
          key: "facebook_url",
          width: "16%",
        },
        {
          title: (
            <SortableHeader
              title="Twitter URL"
              field="twitter_url"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "twitter_url",
          key: "twitter_url",
          width: "16%",
        },
      ],
      [filters.sortBy, filters.sortOrder, handleSortChange]
    );

    const buildCompanySections = useCallback(
      (company: any): EntityDetailSection[] => {
        const assignedUserName =
          typeof company.assigned_user === "object" && company.assigned_user
            ? company.assigned_user.full_name || company.assigned_user.name
            : typeof company.assigned_user === "number"
              ? `User ID: ${company.assigned_user}`
              : null;
        const address = [company.address, company.city, company.state]
          .filter((part) => part && String(part).trim() !== "")
          .join(", ");

        return [
          {
            title: "Company Info",
            icon: "ti ti-building",
            fields: [
              { label: "Company Name", value: company.company_name, fullWidth: true },
              { label: "Industry Type", value: company.industry_type_display_name },
              { label: "Business Type", value: company.business_type_display_name },
              { label: "Employee Count", value: company.employee_count },
              { label: "Annual Revenue", value: company.annual_revenue },
            ],
          },
          {
            title: "Contact Details",
            icon: "ti ti-address-book",
            fields: [
              { label: "Website", value: company.website },
              { label: "Phone", value: company.phone },
              { label: "Address", value: address || null, fullWidth: true },
              { label: "Country", value: company.country },
              { label: "Zip Code", value: company.zip_code },
            ],
          },
          {
            title: "Ownership",
            icon: "ti ti-users",
            fields: [
              {
                label: "Assigned Team",
                value:
                  company.assigned_team?.team_name ||
                  company.assigned_team?.name,
              },
              { label: "Assigned User", value: assignedUserName },
            ],
          },
          {
            title: "Social Links",
            icon: "bi bi-browser-chrome",
            fields: [
              { label: "LinkedIn", value: company.linkedin_url },
              { label: "Facebook", value: company.facebook_url },
              { label: "Twitter", value: company.twitter_url },
            ],
          },
          {
            title: "Timestamps",
            icon: "ti ti-clock",
            fields: [
              {
                label: "Created",
                value: company.created_datetime
                  ? formatCustomDateTime(company.created_datetime)
                  : null,
              },
              {
                label: "Updated",
                value: company.updated_datetime
                  ? formatCustomDateTime(company.updated_datetime)
                  : null,
              },
            ],
          },
        ];
      },
      []
    );

    const columns = useMemo(
      () => [
        {
          title: (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id="select-all"
                checked={!!selectAll}
                onChange={handleSelectAll}
              />
            </div>
          ),
          dataIndex: "checkbox",
          key: "checkbox",
          width: "5%",
          render: (text: any, record: any) => (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id={`select-${record.company_id}`}
                checked={
                  record.company_id
                    ? selectedItems.includes(record.company_id)
                    : false
                }
                onChange={() =>
                  record.company_id && handleSelectItem(record.company_id)
                }
              />
            </div>
          ),
        },

        ...getSortedAvailableColumns()
          .filter((col) => columnVisibility[col.key])
          .map((col) =>
            allAvailableColumns.find((allCol) => allCol.key === col.key)
          )
          .filter(Boolean),
        {
          title: "Action",
          key: "action",
          render: (text: any, record: any) => (
            <div className="d-flex align-items-center justify-content-end gap-2">
              <Link
                to="#"
                className="action-icon"
                onClick={(e) => {
                  e.preventDefault();
                  setDetailsCompany(record);
                }}
              >
                <i className="ti ti-eye text-blue-light" />
              </Link>
              <div className="dropdown table-action">
                <Link
                  to="#"
                  className="action-icon dropdown-toggle"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-boundary="viewport"
                  data-bs-popper-config='{"strategy":"fixed"}'
                >
                  <i className="fa fa-ellipsis-v" />
                </Link>
                <div className="dropdown-menu dropdown-menu-end">
                  <Link
                    className="dropdown-item"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePreview(record);
                    }}
                    style={{ pointerEvents: isPreviewing ? "none" : "auto" }}
                  >
                    <i
                      className={`ti ${isPreviewing ? "ti-loader ti-spin" : "ti-info-circle"
                        } text-blue-light`}
                    />{" "}
                    View Details
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="#"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#offcanvas_edit"
                    onClick={(e) => handleEdit(e, record)}
                  >
                    <i className="ti ti-edit text-blue" />
                    Edit
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDelete(record);
                    }}
                  >
                    <i className="ti ti-trash text-danger" />
                    Delete
                  </Link>
                </div>
              </div>
            </div>
          ),
        },
      ],
      [
        columnVisibility,
        selectedItems,
        selectAll,
        handleSelectAll,
        handleSelectItem,
        getSortedAvailableColumns,
        allAvailableColumns,
        handleEdit,
        handleDelete,
        handlePreview,
        isPreviewing,
      ]
    );

    useEffect(() => {
      if (isInitialized && backgroundLoading) {
        const fetchBackgroundData = async () => {
          try {
          } catch (error) {
            console.error("Background API error:", error);
          } finally {
            clearBackgroundLoading?.();
          }
        };

        fetchBackgroundData();
      }
    }, [isInitialized, backgroundLoading, clearBackgroundLoading]);


    return (
      <>
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                {/* Page Header */}
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title">
                        Companies
                        <span className="count-title">
                          {filters.searchTerm !== "" ||
                            filters.dateRange[0] !== null ||
                            filters.dateRange[1] !== null ||
                            filters.industryFilter.length > 0 ||
                            filters.countryFilter.length > 0 ||
                            isFilterActive
                            ? totalFilteredCount
                            : pagination.totalCount}
                        </span>
                      </h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Page Header */}

                <div className="card">
                  <div className="card-header">
                    <div className="row align-items-center">
                      <div className="col">
                        <ListFilterChips
                          entityType="company"
                          filters={{ ...filters, ...additionalFilters }}
                          onRemove={handleRemoveListFilters}
                          onClearAll={() => applyListFilters(getClearedListFilters(), {})}
                          disabled={loading}
                          className="mb-0"
                        />
                      </div>
                      <div className="col-sm-auto">
                        <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                          {selectedItems.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-danger ms-3"
                              onClick={handleBulkDelete}
                              disabled={isBulkDeleting}
                            >
                              <i className="ti ti-trash me-2" />
                              Delete Selected ({selectedItems.length})
                            </button>
                          )}{" "}
                          &nbsp; &nbsp;
                          <ExportComponent
                            entityType="company"
                            filters={{ ...filters, ...additionalFilters }}
                          />
                          <Link
                            to="#"
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#add_company"
                            onClick={() => setIsAddCompanyOpen(true)}
                          >
                            <i className="ti ti-square-rounded-plus me-2" />
                            Add Company
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={companies}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      sortBy={filters.sort_by}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                    {/* Right Side Icons */}
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <SortByDropdown
                        value={filters.sort_by}
                        onChange={(sortBy) =>
                          setFilters((prev) => ({ ...prev, sort_by: sortBy }))
                        }
                      />
                      {/* Filter Dropdown */}
                      <CompanyContactFilterDropdown
                        onFilterChange={handleAdditionalFilterChange}
                        currentFilters={additionalFilters}
                        loading={loading}
                        entityType="company"
                      />
                      {/* Manage Columns */}
                      <ManageColumns
                        availableColumns={availableColumns}
                        columnVisibility={columnVisibility}
                        onColumnVisibilityChange={handleColumnVisibilityChange}
                        onResetColumns={handleResetColumns}
                        onReorderColumns={setOrderedAvailableColumns}
                      />

                      {/* View Toggle Icons */}
                      <div className="view-icons">
                        <Link
                          to="#"
                          className={currentView === "list" ? "active" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            onViewSwitch("list");
                          }}
                        >
                          <i className="ti ti-list-tree" />
                        </Link>
                        <Link
                          to="#"
                          className={currentView === "grid" ? "active" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            onViewSwitch("grid");
                          }}
                        >
                          <i className="ti ti-grid-dots" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  {loading ? (
                    <div className="card-body">
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: "60vh" }}
                      >
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            Loading Companies...
                          </small>
                        </div>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="card-body">
                      <div className="alert alert-danger text-center">
                        <i className="ti ti-alert-circle me-2"></i>
                        {error}
                      </div>
                    </div>
                  ) : (
                    <div className="card-body">
                      <div
                        className="table-responsive"
                        style={{ position: "relative" }}
                      >
                        {isFilterLoading && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(255, 255, 255, 1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 10,
                              minHeight: "200px",
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              <span>Filtering results...</span>
                            </div>
                          </div>
                        )}
                        <Table
                          columns={columns}
                          dataSource={paginatedCompanies}
                          rowKey={(record) => record.company_id}
                          rowSelection={false}
                          loading={loading}
                          pagination={false}
                        />
                        <TablePagination
                          currentPage={
                            isFilterActive
                              ? currentPage
                              : pagination.currentPage || currentPage
                          }
                          pageSize={12}
                          totalItems={
                            isFilterActive
                              ? totalFilteredCount
                              : pagination.totalCount
                          }
                          filteredItems={totalFilteredCount}
                          isFiltered={
                            filters.searchTerm !== "" ||
                            filters.dateRange[0] !== null ||
                            filters.dateRange[1] !== null ||
                            filters.industryFilter.length > 0 ||
                            filters.countryFilter.length > 0
                          }
                          onPageChange={handlePageChange}
                          onPageSizeChange={() => { }}
                          entityName="Companies"
                          loading={loading}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Company Offcanvas */}
        <div
          className="offcanvas offcanvas-end company-form-offcanvas"
          tabIndex={-1}
          id="add_company"

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">Add New Company</h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={() => setSelectedCompany(null)}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          {isAddCompanyOpen && (
            <CompanyForm
              mode="add"
              company={null}
              formId="add_company_form"
              onSubmit={handleCreateSubmit}
              onCancel={() => {
                setSelectedCompany(null);
                closeOffcanvas("add_company");
              }}
              loading={isSubmitting}
            />
          )}
        </div>

        {/* Edit Company Offcanvas */}
        <div
          className="offcanvas offcanvas-end company-form-offcanvas"
          tabIndex={-1}
          id="offcanvas_edit"

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">Edit Company</h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          {isEditCompanyOpen && (
            <CompanyForm
              mode="edit"
              company={selectedCompany}
              formId="edit_company_form"
              onSubmit={handleEditSubmit}
              onCancel={() => {
                setSelectedCompany(null);
                closeOffcanvas("offcanvas_edit");
              }}
              loading={isSubmitting}
              isMainApiCompleted={isInitialized}
            />
          )}
        </div>



        {detailsCompany && (
          <EntityDetailsModal
            isOpen={!!detailsCompany}
            onClose={() => setDetailsCompany(null)}
            title={detailsCompany.company_name}
            subtitle={detailsCompany.industry_type_display_name || undefined}
            eyebrow="Company"
            avatarUrl={detailsCompany.company_logo || null}
            avatarIcon="ti ti-building-community"
            badge={
              detailsCompany.business_type_display_name
                ? {
                  label: detailsCompany.business_type_display_name,
                  tone: "info",
                }
                : undefined
            }
            sections={buildCompanySections(detailsCompany)}
            onEdit={() => {
              const company = detailsCompany;
              setDetailsCompany(null);
              handleEdit(
                { preventDefault: () => { }, stopPropagation: () => { } } as any,
                company
              );
              openOffcanvas("offcanvas_edit");
            }}
          />
        )}

        {/* Bulk Delete Confirmation Modal */}

      </>
    );
  }
);

CompaniesListView.displayName = "CompaniesListView";

export default CompaniesListView;
